import React from "react";
import { Search, SlidersHorizontal, ArrowUpDown, Calendar as CalendarIcon, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { useI18n } from "@/contexts/I18nContext";

/**
 * Reusable table toolbar with search, filters, sort, date range, and page-size controls.
 * All sections are optional — pass only the props for the controls you want to show.
 */
export default function TableToolbar({
    search,
    onSearchChange,
    searchPlaceholder,

    filters,            // [{ key, label, options: [{value,label}] }]
    filterValues,       // { [key]: value }
    onFilterChange,     // (key, value) => void
    onClearFilters,

    sortOptions,        // [{ value, label }]
    sortValue,
    onSortChange,

    showDates = false,
    dateRange,          // { from, to }
    onDateRangeChange,  // ({from, to}) => void

    pageSize,
    onPageSizeChange,
    pageSizeOptions = [10, 25, 50, 100],

    rightSlot,          // Optional: render any extra control on the right
}) {
    const { t } = useI18n();

    const activeFilterCount = filterValues
        ? Object.values(filterValues).filter((v) => v !== undefined && v !== null && v !== "").length
        : 0;
    const hasDateRange = !!(dateRange && (dateRange.from || dateRange.to));

    return (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            {/* Search */}
            {onSearchChange && (
                <div className="relative flex-1 min-w-[200px]">
                    <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                    <Input
                        value={search || ""}
                        onChange={(e) => onSearchChange(e.target.value)}
                        placeholder={searchPlaceholder || t("table.searchPlaceholder")}
                        className="ps-10"
                        data-testid="table-toolbar-search"
                    />
                </div>
            )}

            {/* Filters */}
            {filters && filters.length > 0 && (
                <Popover>
                    <PopoverTrigger asChild>
                        <Button variant="outline" data-testid="table-toolbar-filters">
                            <SlidersHorizontal className="h-4 w-4 me-2" />
                            {t("table.filters")}
                            {activeFilterCount > 0 && (
                                <span className="ms-2 inline-flex items-center justify-center h-5 min-w-5 px-1.5 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold">
                                    {activeFilterCount}
                                </span>
                            )}
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-72" align="end">
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <p className="text-sm font-semibold">{t("table.filters")}</p>
                                {activeFilterCount > 0 && onClearFilters && (
                                    <button
                                        type="button"
                                        onClick={onClearFilters}
                                        className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center"
                                        data-testid="table-toolbar-clear-filters"
                                    >
                                        <X className="h-3 w-3 me-1" />
                                        {t("table.clearAll")}
                                    </button>
                                )}
                            </div>
                            {filters.map((f) => (
                                <div key={f.key}>
                                    <Label className="text-xs text-muted-foreground">{f.label}</Label>
                                    <Select
                                        value={filterValues?.[f.key] || "__all__"}
                                        onValueChange={(v) => onFilterChange?.(f.key, v === "__all__" ? "" : v)}
                                    >
                                        <SelectTrigger className="mt-1" data-testid={`filter-${f.key}`}>
                                            <SelectValue placeholder={t("table.allValues")} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="__all__">{t("table.allValues")}</SelectItem>
                                            {f.options.map((opt) => (
                                                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            ))}
                        </div>
                    </PopoverContent>
                </Popover>
            )}

            {/* Sort */}
            {sortOptions && sortOptions.length > 0 && onSortChange && (
                <Popover>
                    <PopoverTrigger asChild>
                        <Button variant="outline" data-testid="table-toolbar-sort">
                            <ArrowUpDown className="h-4 w-4 me-2" />
                            {t("table.sortBy")}
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-56" align="end">
                        <div className="space-y-1">
                            <p className="text-sm font-semibold mb-2">{t("table.sortBy")}</p>
                            {sortOptions.map((opt) => (
                                <button
                                    type="button"
                                    key={opt.value}
                                    onClick={() => onSortChange(opt.value)}
                                    className={`w-full text-start text-sm rounded-md px-2 py-1.5 hover:bg-accent ${
                                        sortValue === opt.value ? "bg-accent font-semibold" : ""
                                    }`}
                                    data-testid={`sort-option-${opt.value}`}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </PopoverContent>
                </Popover>
            )}

            {/* Dates */}
            {showDates && onDateRangeChange && (
                <Popover>
                    <PopoverTrigger asChild>
                        <Button variant="outline" data-testid="table-toolbar-dates">
                            <CalendarIcon className="h-4 w-4 me-2" />
                            {hasDateRange ? t("table.dateRangeSet") : t("table.selectDates")}
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-72" align="end">
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <p className="text-sm font-semibold">{t("table.dateRange")}</p>
                                {hasDateRange && (
                                    <button
                                        type="button"
                                        onClick={() => onDateRangeChange({ from: "", to: "" })}
                                        className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center"
                                    >
                                        <X className="h-3 w-3 me-1" />
                                        {t("table.clear")}
                                    </button>
                                )}
                            </div>
                            <div>
                                <Label className="text-xs text-muted-foreground">{t("table.from")}</Label>
                                <Input
                                    type="date"
                                    value={dateRange?.from || ""}
                                    onChange={(e) => onDateRangeChange({ ...(dateRange || {}), from: e.target.value })}
                                    className="mt-1"
                                    data-testid="date-from"
                                />
                            </div>
                            <div>
                                <Label className="text-xs text-muted-foreground">{t("table.to")}</Label>
                                <Input
                                    type="date"
                                    value={dateRange?.to || ""}
                                    onChange={(e) => onDateRangeChange({ ...(dateRange || {}), to: e.target.value })}
                                    className="mt-1"
                                    data-testid="date-to"
                                />
                            </div>
                        </div>
                    </PopoverContent>
                </Popover>
            )}

            {/* Page size */}
            {onPageSizeChange && (
                <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground hidden sm:inline">{t("table.show")}</span>
                    <Select
                        value={String(pageSize || pageSizeOptions[0])}
                        onValueChange={(v) => onPageSizeChange(Number(v))}
                    >
                        <SelectTrigger className="w-[80px]" data-testid="table-toolbar-page-size">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {pageSizeOptions.map((n) => (
                                <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            )}

            {rightSlot}
        </div>
    );
}
